import { useState } from 'react';
import {
  Button, IconButton, Tooltip, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  Stack, TextField, MenuItem, Grid,
} from '@mui/material';
import { Add, Visibility, Delete } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { StatCard } from '../../components/StatCard';
import { DataTable, type Column } from '../../components/DataTable';
import { TableToolbar } from '../../components/TableToolbar';
import { StatusBadge } from '../../components/StatusBadge';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { EditableItemsTable } from '../../components/EditableItemsTable';
import { WorkOrderDetailDialog } from './WorkOrderDetailDialog';
import { workOrdersApi, type WorkOrder, type WoItem } from '../../api/workOrders.api';
import { omsApi } from '../../api/oms.api';
import { sitesApi } from '../../api/sites.api';
import { vendorsApi } from '../../api/vendors.api';
import { techniciansApi } from '../../api/technicians.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

type Row = Record<string, unknown>;
const ITEM_COLUMNS = [
  { key: 'description', label: 'Description' },
  { key: 'unit', label: 'Unit', width: 90 },
  { key: 'quantity', label: 'Qty', type: 'number' as const, width: 90 },
  { key: 'rate', label: 'Rate', type: 'number' as const, width: 120 },
];

export function WorkOrderListPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [detailId, setDetailId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [omsId, setOmsId] = useState<number | ''>('');
  const [siteId, setSiteId] = useState<number | ''>('');
  const [vendorId, setVendorId] = useState<number | ''>('');
  const [technicianId, setTechnicianId] = useState<number | ''>('');
  const [title, setTitle] = useState('');
  const [items, setItems] = useState<Row[]>([{ description: '', unit: '', quantity: 0, rate: 0 }]);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<WorkOrder | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const dashboard = useQuery({ queryKey: ['wo-dashboard'], queryFn: workOrdersApi.dashboard });
  const { data, isLoading } = useQuery({ queryKey: ['work-orders', page, search], queryFn: () => workOrdersApi.list({ page, limit: 25, search: search || undefined }) });
  const omsOptions = useQuery({ queryKey: ['oms', 'forWo'], queryFn: () => omsApi.list({ limit: 100 }), enabled: createOpen });
  const siteOptions = useQuery({ queryKey: ['sites', 'forWo'], queryFn: () => sitesApi.list({ limit: 100 }), enabled: createOpen });
  const vendorOptions = useQuery({ queryKey: ['vendors', 'forWo'], queryFn: () => vendorsApi.list({ limit: 100 }), enabled: createOpen });
  const techOptions = useQuery({ queryKey: ['technicians', 'forWo'], queryFn: () => techniciansApi.list({ limit: 100 }), enabled: createOpen });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ['work-orders'] }); qc.invalidateQueries({ queryKey: ['wo-dashboard'] }); };

  const resetForm = () => { setOmsId(''); setSiteId(''); setVendorId(''); setTechnicianId(''); setTitle(''); setItems([{ description: '', unit: '', quantity: 0, rate: 0 }]); };

  const createMut = useMutation({
    mutationFn: () => workOrdersApi.create({
      omsId: Number(omsId), siteId: siteId ? Number(siteId) : null, vendorId: vendorId ? Number(vendorId) : null,
      technicianId: technicianId ? Number(technicianId) : null, title,
      items: items.filter((r) => r.description && r.unit).map((r): WoItem => ({ description: String(r.description), unit: String(r.unit), quantity: Number(r.quantity), rate: Number(r.rate) })),
    }),
    onSuccess: () => { setCreateOpen(false); resetForm(); invalidate(); },
    onError: (e) => setError(extractApiError(e)),
  });
  const deleteMut = useMutation({ mutationFn: (id: number) => workOrdersApi.remove(id), onSuccess: () => { setDeleting(null); invalidate(); }, onError: (e) => { setToast(extractApiError(e)); setDeleting(null); } });

  const columns: Column<WorkOrder>[] = [
    { key: 'code', label: 'WO No.', width: 150 },
    { key: 'title', label: 'Title' },
    { key: 'totalAmount', label: 'Amount', align: 'right', render: (r) => `₹${r.totalAmount.toLocaleString()}` },
    { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ];

  return (
    <>
      <PageHeader
        title="Work Orders"
        subtitle="Field execution assigned to vendors and technicians."
        action={hasPermission('wo.create') && <Button startIcon={<Add />} variant="contained" onClick={() => { resetForm(); setError(null); setCreateOpen(true); }}>New Work Order</Button>}
      />

      <Grid container spacing={2} mb={1}>
        <Grid item xs={12} sm={4}><StatCard color="#1e40af" label="Total WO Value" value={`₹${(dashboard.data?.totalValue ?? 0).toLocaleString()}`} /></Grid>
        <Grid item xs={6} sm={4}><StatCard color="#0d9488" label="In Progress" value={dashboard.data?.byStatus?.in_progress ?? 0} /></Grid>
        <Grid item xs={6} sm={4}><StatCard color="#16a34a" label="Completed" value={(dashboard.data?.byStatus?.completed ?? 0) + (dashboard.data?.byStatus?.closed ?? 0)} /></Grid>
      </Grid>

      <TableToolbar search={search} onSearch={(v) => { setSearch(v); setPage(1); }} searchPlaceholder="Search WO code / title" />
      <DataTable
        columns={columns} rows={data?.data ?? []} loading={isLoading}
        page={page} limit={25} total={data?.meta?.total ?? 0} onPageChange={setPage} onLimitChange={() => undefined} rowKey={(r) => r.id}
        emptyTitle="No work orders yet"
        actions={(r) => (
          <>
            <Tooltip title="Open"><IconButton size="small" onClick={() => setDetailId(r.id)}><Visibility fontSize="small" /></IconButton></Tooltip>
            {hasPermission('wo.delete') && ['draft', 'rejected', 'cancelled'].includes(r.status) && <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleting(r)}><Delete fontSize="small" /></IconButton></Tooltip>}
          </>
        )}
      />

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>New Work Order</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField select label="OMS" value={omsId} onChange={(e) => setOmsId(Number(e.target.value))} required fullWidth>
              {(omsOptions.data?.data ?? []).map((o) => <MenuItem key={o.id} value={o.id}>{o.omsNumber} — {o.title}</MenuItem>)}
            </TextField>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField select label="Site (optional)" value={siteId} onChange={(e) => setSiteId(e.target.value ? Number(e.target.value) : '')} fullWidth>
                <MenuItem value="">— None —</MenuItem>
                {(siteOptions.data?.data ?? []).map((s) => <MenuItem key={s.id} value={s.id}>{s.siteName}</MenuItem>)}
              </TextField>
              <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} required fullWidth />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField select label="Vendor" value={vendorId} onChange={(e) => setVendorId(e.target.value ? Number(e.target.value) : '')} fullWidth helperText="Vendor and/or technician required">
                <MenuItem value="">— None —</MenuItem>
                {(vendorOptions.data?.data ?? []).map((v) => <MenuItem key={v.id} value={v.id}>{v.vendorName}</MenuItem>)}
              </TextField>
              <TextField select label="Technician" value={technicianId} onChange={(e) => setTechnicianId(e.target.value ? Number(e.target.value) : '')} fullWidth>
                <MenuItem value="">— None —</MenuItem>
                {(techOptions.data?.data ?? []).map((t) => <MenuItem key={t.id} value={t.id}>{t.technicianName}</MenuItem>)}
              </TextField>
            </Stack>
            <EditableItemsTable columns={ITEM_COLUMNS} rows={items} onChange={setItems} newRow={() => ({ description: '', unit: '', quantity: 0, rate: 0 })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!omsId || !title || (!vendorId && !technicianId) || !items.some((r) => r.description && r.unit)} onClick={() => createMut.mutate()}>Create</Button>
        </DialogActions>
      </Dialog>

      <WorkOrderDetailDialog woId={detailId} onClose={() => { setDetailId(null); invalidate(); }} />
      <ConfirmDialog open={Boolean(deleting)} title="Delete work order" message={`Delete ${deleting?.code}?`} confirmLabel="Delete" onCancel={() => setDeleting(null)} onConfirm={() => deleting && deleteMut.mutate(deleting.id)} />
      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)}><Alert severity="warning" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}
